import {
  CLIENT_API_ENDPOINTS,
  isAppWebView,
  isBotUserAgent,
} from '@/shared/lib'

import { DATADOG_CONFIG } from './config'

import type { datadogRum as DatadogRum } from '@datadog/browser-rum'

/**
 * 무시하는 에러 목록 (이벤트 전송 전에 막음)
 */
const IGNORED_ERROR_MESSAGES = [
  'Script error.', // 외부 도메인 스크립트 에러
  'Ignored attempt to cancel a touchmove event', // 지도 SDK(네이버·카카오)의 touchmove 인터벤션
  '(installations/', // Firebase Installations 토큰 발급 실패
]

// KCP 결제창 스크립트(kcp_jquery)의 passive 리스너 인터벤션 — 우리 코드가 아니다.
const PASSIVE_LISTENER_INTERVENTION =
  'Unable to preventDefault inside passive event listener'
const KCP_SCRIPT_HOST = 'spay.kcp.co.kr'

const isIgnoredError = (error: { message: string; stack?: string }) =>
  IGNORED_ERROR_MESSAGES.some(message => error.message.includes(message)) ||
  (error.message.includes(PASSIVE_LISTENER_INTERVENTION) &&
    !!error.stack?.includes(KCP_SCRIPT_HOST))

/**
 * 뷰 이름을 react 라우트 단위로 재정의 - 데이터독이 자동으로 만드는 뷰 이름이 우리 구조와 맞지 않음.
 * ex) 정비소 상세 /place/abc, /place/abd 가 다른 페이지로 찍힘.
 * @param url
 */
const DYNAMIC_VIEW_PATHS: [RegExp, string][] = [
  [/^\/place\/(?!search$)[^/]+$/, '/place/?'],
  [/^\/place-search\/map\/[^/]+$/, '/place-search/map/?'],
  [/^\/place-search\/(?!map$)[^/]+$/, '/place-search/?'],
  [/^\/partners\/on-site\/payment\/[^/]+$/, '/partners/on-site/payment/?'],
  [/^\/bridge\/(tire|tire-v2)\/[^/]+$/, '/bridge/$1/?'],
  [/^\/modoocar\/(?!home$|rank$|search$)[^/]+$/, '/modoocar/?'],
  [/^\/car\/(?!search$)[^/]+$/, '/car/?'],
  [/^\/car\/[^/]+\/[^/]+$/, '/car/?/?'],
  [/^\/car\/[^/]+\/[^/]+\/[^/]+$/, '/car/?/?/?'],
  [/^\/car\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/, '/car/?/?/?/?'],
]

const getGroupedViewName = (url: string) => {
  const [, basePath = '', path = ''] =
    new URL(url).pathname.match(/^(.*?\/v2)(\/.*)?$/) ?? []
  const matched = DYNAMIC_VIEW_PATHS.find(([pattern]) => pattern.test(path))
  if (!matched) return null

  const [pattern, groupedPath] = matched
  return basePath + path.replace(pattern, groupedPath)
}

/**
 * RUM 이벤트에 로그인된 식별자의 ServerId를 심는다.
 * 비로그인·만료 쿠키는 식별 전용 BFF가 204로 끝내므로 401이 쌓이지 않는다.
 */
let isIdentifying = false
const USER_ID_STORAGE_KEY = 'dd_rum_user_id'
const NO_CONTENT_STATUS = 204

export const identifyDatadogUser = async () => {
  if (!rum || isIdentifying) return

  isIdentifying = true
  try {
    const cachedId = sessionStorage.getItem(USER_ID_STORAGE_KEY)
    if (cachedId) {
      rum.setUser({ id: cachedId })
      return
    }

    const response = await fetch(CLIENT_API_ENDPOINTS.USER.RUM_ID)
    if (!response.ok || response.status === NO_CONTENT_STATUS) return

    const { serverId } = (await response.json()) as { serverId?: number }
    if (!serverId) return

    const id = String(serverId)
    sessionStorage.setItem(USER_ID_STORAGE_KEY, id)
    rum.setUser({ id })
  } catch {
    // 식별 실패는 RUM 수집 자체에는 영향이 없음.
  } finally {
    isIdentifying = false
  }
}

/**
 * 데이터독 RUM 초기화
 */
let rum: typeof DatadogRum | null = null
let isInitializing = false

export const initDatadogRum = async () => {
  if (rum || isInitializing) return
  if (typeof window === 'undefined') return
  if (window.location.hostname === 'localhost') return
  if (isBotUserAgent()) return
  if (!DATADOG_CONFIG.applicationId || !DATADOG_CONFIG.clientToken) {
    return
  }

  isInitializing = true
  const { datadogRum } = await import('@datadog/browser-rum')

  datadogRum.init({
    applicationId: DATADOG_CONFIG.applicationId,
    clientToken: DATADOG_CONFIG.clientToken,
    site: DATADOG_CONFIG.site,
    service: DATADOG_CONFIG.service,
    env: DATADOG_CONFIG.env,
    version: DATADOG_CONFIG.version,
    sessionSampleRate: DATADOG_CONFIG.sessionSampleRate,
    sessionReplaySampleRate: DATADOG_CONFIG.sessionReplaySampleRate,
    startSessionReplayRecordingManually:
      DATADOG_CONFIG.startSessionReplayRecordingManually,
    trackUserInteractions: DATADOG_CONFIG.trackUserInteractions,
    trackResources: DATADOG_CONFIG.trackResources,
    trackLongTasks: DATADOG_CONFIG.trackLongTasks,
    defaultPrivacyLevel: DATADOG_CONFIG.defaultPrivacyLevel,
    allowedTracingUrls: [/https:\/\/.*\.(macarong\.net|mycle\.co\.kr)/],
    beforeSend: event => {
      if (event.type === 'error' && isIgnoredError(event.error)) return false

      const viewName = getGroupedViewName(event.view.url)
      if (viewName) event.view.name = viewName

      return true
    },
  })
  // 앱 웹뷰 에러와 브라우저 에러를 바로 가를 수 있게 모든 이벤트에 붙인다.
  datadogRum.setGlobalContextProperty('isAppWebView', isAppWebView())

  rum = datadogRum
}

export const setDatadogUser = (user: {
  id: string
  name?: string
  email?: string
  [key: string]: unknown
}) => {
  rum?.setUser(user)
}

export const clearDatadogUser = () => {
  rum?.clearUser()
  try {
    sessionStorage.removeItem(USER_ID_STORAGE_KEY)
  } catch {
    // 스토리지 접근이 막힌 환경에서도 로그아웃 흐름은 이어져야 한다.
  }
}

export const getDatadogSessionId = () => rum?.getInternalContext()?.session_id

export const trackDatadogAction = (name: string, context?: object) => {
  rum?.addAction(name, context)
}

export const trackDatadogError = (error: Error, context?: object) => {
  rum?.addError(error, context)
}
